import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor } from '@tiptap/react';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import { ThemeResult } from '../utils/questionExtractor';
import { fetchSummaries } from '../utils/fetchSummaries';
import { apiFetch } from '../utils/api';
import { toast } from 'sonner';

interface MarkdownStorage {
  markdown?: {
    getMarkdown: () => string;
  };
}

export function useSummaryData(caseId: string | undefined, menuRef: React.RefObject<HTMLDivElement | null>) {
  const navigate = useNavigate();
  const [extractResults, setExtractResults] = useState<ThemeResult[]>([]);
  const [currentThemeIndex, setCurrentThemeIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  const previousResultsRef = useRef<ThemeResult[]>([]);
  const previousContentRef = useRef<string>('');
  const previousThemeIndexRef = useRef<number>(-1);
  const isReloadingAfterSaveRef = useRef<boolean>(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Markdown.configure({}),
      BubbleMenu.configure({
        element: menuRef.current!,
        shouldShow: ({ editor, view, state }) => {
          if (!view.hasFocus()) return false;
          if (state.selection.empty) return false;
          if (editor.isActive('codeBlock')) return false;
          return editor.isEditable;
        },
        options: { placement: 'top', offset: 8 },
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'focus:outline-none focus:ring-0 focus-visible:outline-none prose prose-sm ProseMirror [&_li>p]:m-0 [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:m-0 [&_ul]:m-0  max-w-none',
      },
    },
  });

  // Force re-render on editor changes
  const [, force] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const tick = () => force(x => x + 1);
    editor.on('selectionUpdate', tick).on('transaction', tick).on('update', tick);
    return () => {
      editor.off('selectionUpdate', tick).off('transaction', tick).off('update', tick);
    };
  }, [editor]);

  // Poll for summary data
  useEffect(() => {
    let pollId: ReturnType<typeof setInterval> | null = null;

    const fetchAndSetResults = async () => {
      if (isReloadingAfterSaveRef.current) return;

      try {
        if (caseId) {
          const results = await fetchSummaries(caseId);

          if (results && results[0]?.text) {
            const previousResults = previousResultsRef.current;
            const hasChanged = previousResults.length !== results.length ||
              previousResults.some((existing: ThemeResult, index: number) => {
                const newResult = results[index];
                return !newResult ||
                  existing.text !== newResult.text ||
                  existing.workflow_status !== newResult.workflow_status ||
                  existing.page_name !== newResult.page_name;
              });

            if (hasChanged) {
              setCurrentThemeIndex((prevIndex) => {
                const validIndex = Math.min(prevIndex, results.length - 1);
                previousThemeIndexRef.current = validIndex;
                return validIndex;
              });
              setExtractResults(results);
              previousResultsRef.current = results;
            }

            const hasEnqueuedWorkflows = results.some((r: ThemeResult) => r.workflow_status === 'enqueued');

            if (pollId && !hasEnqueuedWorkflows) {
              clearInterval(pollId);
              pollId = null;
            }
          }
        }
      } catch (err) {
        console.error('Error fetching extract results:', err);
        if (pollId) {
          clearInterval(pollId);
          pollId = null;
        }
      }
    };

    fetchAndSetResults();
    pollId = setInterval(fetchAndSetResults, 10000);

    return () => {
      if (pollId) clearInterval(pollId);
    };
  }, [caseId, editor]);

  // Sync editor content when data or theme changes
  useEffect(() => {
    if (isReloadingAfterSaveRef.current) return;
    if (!editor || extractResults.length === 0 || !extractResults[currentThemeIndex]) return;

    const newContent = extractResults[currentThemeIndex].text || '';

    const themeChanged = previousThemeIndexRef.current !== currentThemeIndex;
    if (themeChanged) {
      previousContentRef.current = '';
      previousThemeIndexRef.current = currentThemeIndex;
    }

    if (themeChanged || newContent !== previousContentRef.current) {
      requestAnimationFrame(() => {
        if (editor && !editor.isDestroyed) {
          editor.commands.setContent(newContent);
          previousContentRef.current = newContent;
        }
      });
    }
  }, [editor, extractResults, currentThemeIndex]);

  // Navigation
  const handlePreviousTheme = useCallback(() => {
    if (currentThemeIndex > 0) setCurrentThemeIndex(currentThemeIndex - 1);
  }, [currentThemeIndex]);

  const handleNextTheme = useCallback(() => {
    if (currentThemeIndex < extractResults.length - 1) setCurrentThemeIndex(currentThemeIndex + 1);
  }, [currentThemeIndex, extractResults.length]);

  const handleThemeSelect = useCallback((index: number) => {
    setCurrentThemeIndex(index);
  }, []);

  // Complete conversation
  const handleCompleteConversation = useCallback(async () => {
    if (!caseId) {
      toast.error('Geen case ID beschikbaar', {
        description: 'Kan de vragenlijst niet versturen zonder case ID.',
        duration: 3000,
      });
      return;
    }

    setIsCompleting(true);
    try {
      const response = await apiFetch(`/conversation/${caseId}/complete`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to complete conversation: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      await response.json();
      toast.success('Vragenlijst verstuurd!', {
        description: 'De vragenlijst is succesvol verstuurd.',
        duration: 3000,
      });
      navigate('/');
    } catch (error) {
      console.error('Error completing conversation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Onbekende fout opgetreden';
      toast.error('Fout bij het versturen', {
        description: errorMessage.includes('Failed to complete')
          ? 'Kon de vragenlijst niet versturen. Controleer je internetverbinding en probeer het opnieuw.'
          : errorMessage,
        duration: 6000,
      });
    } finally {
      setIsCompleting(false);
    }
  }, [caseId, navigate]);

  // Save summary
  const handleSaveSummary = useCallback(async () => {
    if (!editor || !extractResults[currentThemeIndex]) return;

    const summaryId = extractResults[currentThemeIndex].id;
    if (!summaryId) {
      console.error('No summary ID available');
      return;
    }

    const markdownStorage = (editor.storage as MarkdownStorage).markdown;
    if (!markdownStorage) {
      toast.error('Fout bij het opslaan', {
        description: 'Markdown-extensie is niet beschikbaar.',
        duration: 6000,
      });
      return;
    }

    const markdownContent = markdownStorage.getMarkdown();

    setIsSaving(true);
    try {
      const response = await apiFetch(`/conversation/summary/${summaryId}`, {
        method: 'PATCH',
        body: JSON.stringify({ summary: markdownContent }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save summary: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      await response.json();
      setShowSaveSuccess(true);

      // Reload summaries after save
      const savedThemeIndex = currentThemeIndex;
      isReloadingAfterSaveRef.current = true;
      try {
        if (caseId) {
          const updatedResults = await fetchSummaries(caseId);
          if (updatedResults && updatedResults.length > 0) {
            const validThemeIndex = Math.max(0, Math.min(savedThemeIndex, updatedResults.length - 1));

            previousThemeIndexRef.current = validThemeIndex;
            previousResultsRef.current = updatedResults;

            setCurrentThemeIndex(validThemeIndex);
            setTimeout(() => {
              setExtractResults(updatedResults);
            }, 0);

            if (updatedResults[validThemeIndex]) {
              const newContent = updatedResults[validThemeIndex].text || '';
              if (editor && !editor.isDestroyed) {
                editor.commands.setContent(newContent);
                previousContentRef.current = newContent;
              }
            }
          }
        }
      } catch (reloadError) {
        console.error('Error reloading summaries after save:', reloadError);
        toast.warning('Samenvatting opgeslagen, maar kon gegevens niet vernieuwen', {
          description: 'Ververs de pagina om de nieuwste gegevens te zien.',
          duration: 5000,
        });
      } finally {
        setTimeout(() => {
          isReloadingAfterSaveRef.current = false;
        }, 100);
      }
    } catch (error) {
      console.error('Error saving summary:', error);
      const errorMessage = error instanceof Error ? error.message : 'Onbekende fout opgetreden';
      toast.error('Fout bij het opslaan', {
        description: errorMessage.includes('Failed to save')
          ? 'Kon de samenvatting niet opslaan. Controleer je internetverbinding en probeer het opnieuw.'
          : errorMessage,
        duration: 6000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [editor, extractResults, currentThemeIndex, caseId]);

  return {
    editor,
    extractResults,
    currentThemeIndex,
    isSaving,
    isCompleting,
    blockOpen,
    setBlockOpen,
    handlePreviousTheme,
    handleNextTheme,
    handleThemeSelect,
    handleCompleteConversation,
    handleSaveSummary,
    showSaveSuccess,
    setShowSaveSuccess,
  };
}
